import React, { useState } from 'react';
import { CheckRounded, KeyboardArrowDownRounded, LayersOutlined, RocketLaunchOutlined, ScienceOutlined } from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import { useAuth } from '../context/AuthContext';

/** Purpose: Build environment labels and presentation metadata. Why: Production must be distinguishable when switching scope. */
const environmentPresentation = (environment) => {
  const isProduction = String(environment).toLowerCase() === 'prod';
  return {
    label: String(environment || 'dev').toUpperCase(),
    name: isProduction ? 'Production' : String(environment).toLowerCase() === 'dev' ? 'Development' : String(environment),
    description: isProduction ? 'Live data and customer traffic' : String(environment).toLowerCase() === 'dev' ? 'Safe space to build and test' : 'Workspace environment',
    Icon: isProduction ? RocketLaunchOutlined : String(environment).toLowerCase() === 'dev' ? ScienceOutlined : LayersOutlined,
    isProduction,
  };
};

/** Purpose: switch the selected bot between permitted environments. Why: DEV/PROD context controls which data and editing actions are available. */
export default function EnvironmentSwitcher() {
  const { selectedBot, selectedEnv, selectEnv, getAvailableEnvs } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const availableEnvs = getAvailableEnvs();

  if (!selectedBot || availableEnvs.length === 0) return null;

  const currentEnvironment = environmentPresentation(selectedEnv || 'dev');
  const open = Boolean(anchorEl);
  /** Purpose: Select an environment and close the menu. Why: Subsequent page requests should use the selected environment. */
  const handleSelect = (environment) => {
    selectEnv(environment);
    setAnchorEl(null);
  };

  return (
    <div className="inline-flex shrink-0">
      <button
        type="button"
        aria-label={`Environment: ${currentEnvironment.label}`}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'environment-switcher-menu' : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className="group inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm transition hover:border-slate-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:border-white/10 dark:bg-slate-900 dark:hover:border-white/20"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Env</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-extrabold tracking-wide ${currentEnvironment.isProduction
          ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-200'
          : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-200'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${currentEnvironment.isProduction ? 'bg-rose-500' : 'bg-blue-500'}`} />
          {currentEnvironment.label}
        </span>
        <KeyboardArrowDownRounded className={`!text-[18px] text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Menu
        id="environment-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1, width: 288, overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 3, boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18)' } }}
        MenuListProps={{ 'aria-label': 'Available environments', sx: { p: 1 } }}
      >
        <div className="px-3 pb-2 pt-2">
          <p className="text-sm font-bold text-slate-900 dark:text-white">Switch environment</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Choose which workspace data you’re viewing.</p>
        </div>
        <div className="mb-1 border-t border-slate-100 dark:border-white/10" />
        {availableEnvs.map((environment) => {
          const presentation = environmentPresentation(environment);
          const selected = String(selectedEnv).toLowerCase() === String(environment).toLowerCase();
          const Icon = presentation.Icon;
          return (
            <MenuItem
              key={environment}
              selected={selected}
              onClick={() => handleSelect(environment)}
              aria-current={selected ? 'true' : undefined}
              className="!mx-1 !my-1 !rounded-xl"
              sx={{ minHeight: 58, '&.Mui-selected': { bgcolor: presentation.isProduction ? 'rgba(244, 63, 94, 0.08)' : 'rgba(37, 99, 235, 0.08)' }, '&.Mui-selected:hover': { bgcolor: presentation.isProduction ? 'rgba(244, 63, 94, 0.12)' : 'rgba(37, 99, 235, 0.12)' } }}
            >
              <span className={`mr-3 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${presentation.isProduction ? 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-400/15 dark:text-blue-200'}`}>
                <Icon fontSize="small" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                  {presentation.name}
                  <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-extrabold tracking-wide ${presentation.isProduction ? 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-400/15 dark:text-blue-200'}`}>{presentation.label}</span>
                </span>
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{presentation.description}</span>
              </span>
              {selected ? <CheckRounded className={`ml-2 !text-[19px] ${presentation.isProduction ? 'text-rose-600 dark:text-rose-300' : 'text-blue-600 dark:text-blue-300'}`} /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
}
